/**
 * Community Profile GraphQL Queries & Mutations
 *
 * All GraphQL operations used by the community profile service.
 * Each export is a gql tagged template consumed by Apollo Client.
 *
 * Organized by domain:
 * - Profile fetching: GET_CURRENT_PROFILE, GET_PROFILE_BY_UID, GET_SETTINGS_PAGE_DATA
 * - Profile mutation: UPDATE_PROFILE
 * - Social: CREATE_FOLLOW, DELETE_FOLLOW, GET_FOLLOWERS, GET_FOLLOWING
 * - Tags / centers: GET_USER_TAG_OPTIONS, GET_USER_META_TAGS, GET_CANONICAL_CENTERS
 * - Playlists: GET_MY_PLAYLISTS, GET_MEMBER_PLAYLISTS, GET_PLAYLISTS_WITH_ITEMS,
 *   SEARCH_MY_PLAYLISTS, GET_DISCOVER_PLAYLISTS, GET_FOLLOWED_PLAYLISTS
 * - Playlist mutations: UPDATE_PLAYLIST_VISIBILITY, DELETE_PLAYLIST,
 *   FOLLOW_PLAYLIST, UNFOLLOW_PLAYLIST
 * - Avatar: CREATE_PROFILE_PICTURE_UPLOAD, CREATE_PROFILE_PICTURE_CONFIRM,
 *   DELETE_PROFILE_PICTURE
 * - Activity: GET_COMMENT_COUNT, GET_COMMUNITY_LEADERBOARD
 * - Watch history: GET_USER_HISTORY
 *
 * @see fragments.ts for shared GraphQL fragments
 */

import { gql } from '@apollo/client'
import {
  CORE_USER_PROFILE_FIELDS,
  USER_PROFILE_SOCIAL_FIELDS,
  USER_PROFILE_SUMMARY_FIELDS,
  PLAYLIST_ITEM_CONTENT_FIELDS,
  MEDIA_TILE_CONTENT_FEATURED,
  ORACLE_CONTENT_FIELDS,
} from './fragments'

export const GET_CURRENT_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileCurrent(
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfileCurrent(language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

/**
 * Lightweight viewer profile query — only the fields a page needs to
 * decide what to render before the heavy social/inProgress payload arrives.
 *
 * Used by CommunityMyLibraryPage to unblock its loading gate.  The full
 * GET_CURRENT_PROFILE fires in parallel for the Currently Watching carousel
 * (inProgress), which is rendered behind its own loading state.
 *
 * Keep this query lean — if a new consumer needs additional fields, prefer
 * a per-page query over expanding this one.
 */
export const GET_CURRENT_PROFILE_LIGHT = gql`
  query UserProfileCurrentLight($language: String) {
    userProfileCurrent(language: $language) {
      uid
      uuid
      displayName
      profileVisibility
      currentlyWatchingVisibility
      watchHistoryVisibility
      avatar {
        url
        small
        card
      }
    }
  }
`

export const GET_PROFILE_BY_UID = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileByUid(
    $uid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfileByUid(uid: $uid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

/**
 * Community-local hydration query used by `useContentHydration` for the
 * /library carousel tiles (system playlists' Watch Later / Favorites and
 * custom playlists' shell-rendered tiles, plus Currently Watching's
 * difficulty-pill enrichment).
 *
 * Why this exists (CO-497): the shared `GET_CONTENT` query in
 * `lib/services/content/query.js` gates the load-bearing series scalars
 * (`contentType`, `subType`, `displayType`, `studio`, `contentAnalytic`)
 * behind `@skip(if: $limitedData)`, and `useContentHydration` was passing
 * `limitedData: true`. That stripped the series shape from the hydrated
 * payload, causing `buildEpisodeOfSeriesTile`'s `series.contentType`
 * guard to bail and the tiles to flip from series-shaped to plain video
 * on the second paint.
 *
 * This query mirrors the Content-side selection the tile builders
 * (`buildTileData`, `buildEpisodeOfSeriesTile`) actually read, with NO
 * `@skip` directives on the series scalars. Includes:
 *   - the load-bearing series projection (contentType, subType,
 *     displayType, studio, contentAnalytic, etc.)
 *   - `featureMedia.mediaAssets.aiMetadata.learnerLevel` — the perf
 *     reason for hydration in the first place (Continue Watching
 *     difficulty pill)
 *   - `contentAssetReferences` (worksheet thumbnails)
 *
 * Cache policy: `cache-and-network` (set by the consumer). Same network
 * shape as `GET_CONTENT` so Apollo's normalized `Content:<id>` entries
 * stay compatible with member-home / discover tiles.
 *
 * Trade-off: a community-owned near-duplicate of `GET_CONTENT`'s tile-
 * shaped subset. The Content team's eventual fix (drop @skip on the
 * five series scalars in the shared query) would let us delete this
 * query — kept narrow on purpose so the diff is small if/when that
 * happens.
 */
export const GET_CONTENT_FOR_TILE_HYDRATION = gql`
  query CommunityContentForTileHydration(
    $titleGroupIds: [String]!,
    $language: [String]!,
    $imageType: [String]!,
    $learnerLevelLanguage: String!,
  ) {
    contentList(
      where: { titleGroupId: { in: $titleGroupIds } }
      language: $language
    ) {
      content {
        id
        contentId
        titleGroupId
        contentType
        title
        path
        shortDescription
        episodeNumber
        seasonNumber
        instructor
        cast
        primaryLanguage
        isNew
        displayType
        subType
        studio
        contentAnalytic {
          votesUpCountTotal
          votesCountTotal
          commentsCountTotal
        }
        featureMedia {
          duration
          mediaId
          mediaAssets {
            language
            mediaReferenceType
            aiMetadata(language: $learnerLevelLanguage) {
              learnerLevel {
                level
              }
            }
          }
          userMediaPosition {
            position
          }
          contentPolicy {
            accessCheck {
              allowed
            }
          }
        }
        imageContentRelationships(
          language: $language
          types: $imageType
        ) {
          type
          image {
            url
          }
        }
        contentAssetReferences(contentAssetReferences: { type: "worksheet" }) {
          asset {
            originalFileName
            language
            renderUrl
            mimeType
            uuid
          }
          contentAssetReferenceType {
            type
          }
        }
        references(typesByTypeTag: true) {
          contentReferenceType { type }
          contentTo(language: $language) {
            title
            path(format: RELATIVE)
            contentType
            contentId
            language
          }
        }
        contentSeries(language: $language) {
          id
          contentId
          contentType
          title
          path(format: RELATIVE)
          shortDescription
          subType
          displayType
          studio
          contentAnalytic {
            votesUpCountTotal
            votesCountTotal
            commentsCountTotal
          }
          imageContentRelationships(
            language: $language
            types: $imageType
          ) {
            type
            image {
              url
            }
          }
          references(typesByTypeTag: true) {
            contentReferenceType { type }
            contentTo(language: $language) {
              title
              path(format: RELATIVE)
              contentType
              contentId
              language
            }
          }
        }
      }
    }
  }
`

/**
 * In Progress carousel — fires after the main profile query lands.
 *
 * Splits the heavy `inProgress` selection out of GET_CURRENT_PROFILE /
 * GET_PROFILE_BY_UID for the profile page. The main profile query renders
 * scalars + system-playlist meta fast (passes includeMemberFields=false,
 * includeWatchHistory=false to skip the heavy arrays); the carousel fills
 * behind its own loading state when this query lands.
 *
 * Uses `userProfileByUid` for both own + member-viewing-other cases —
 * server serves the requesting user's own data when uid=loggedInUid.
 *
 * Anonymous viewers cannot see inProgress (auth-gated), so the consumer
 * skips this query for the public-viewer path.
 */
export const GET_PROFILE_IN_PROGRESS_CAROUSEL = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query UserProfileInProgressCarousel(
    $uuid: ID!
    $contentLanguage: [String]
  ) {
    userProfileByUuid(uuid: $uuid) {
      uuid
      inProgress(limit: 24) {
        mediaId
        uid
        position
        lastPlayed
        watchedCount
        hiddenByUser
        hiddenFromContinue
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
    }
  }
`

/**
 * Watch History carousel — fires after the main profile query lands.
 *
 * Mirrors GET_PROFILE_IN_PROGRESS_CAROUSEL but selects `watchHistory`
 * instead of `inProgress`. Same auth gating (anonymous viewers skip).
 */
export const GET_PROFILE_WATCH_HISTORY_CAROUSEL = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query UserProfileWatchHistoryCarousel(
    $uuid: ID!
    $contentLanguage: [String]
  ) {
    userProfileByUuid(uuid: $uuid) {
      uuid
      watchHistory(limit: 24) {
        mediaId
        uid
        position
        lastPlayed
        watchedSum
        hiddenByUser
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
    }
  }
`

/**
 * In-progress items with a higher limit for the full-page view.
 *
 * The shared USER_PROFILE_SOCIAL_FIELDS fragment caps inProgress at 10 items
 * (enough for the profile carousel preview). This standalone query uses a
 * larger limit so the /currently-watching page can show all in-progress series.
 *
 * The field selection mirrors USER_PROFILE_SOCIAL_FIELDS.inProgress exactly;
 * keep them in sync when the fragment changes.
 */
export const GET_IN_PROGRESS_FULL = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query InProgressFull($language: String, $contentLanguage: [String]) {
    userProfileCurrent(language: $language) {
      uuid
      uid
      inProgress(limit: 24) {
        mediaId
        uid
        position
        lastPlayed
        watchedCount
        hiddenByUser
        hiddenFromContinue
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
    }
  }
`

/**
 * In-progress items via userMediaPositionList with backend filtering.
 *
 * Replaces GET_IN_PROGRESS_FULL (which used the unfiltered inProgress federated
 * field). Backend filters handle: hiddenByUser, lastPlayed > 0, watchedSum >= 15s.
 * Client-side useInProgressEnrichment adds the 95%-completion threshold.
 */
export const GET_IN_PROGRESS_LIST = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query GetInProgressList(
    $limit: Int
    $contentLanguage: [String]
  ) {
    userMediaPositionList(
      userCurrent: true
      publishedMediaOnly: true
      limit: $limit
      order: [["lastPlayed", "DESC"]]
      where: {
        hiddenByUser: { eq: false }
        lastPlayed: { gt: "0" }
        watchedSum: { gte: "15" }
      }
    ) {
      userMediaPositions {
        mediaId
        position
        lastPlayed
        watchedSum
        hiddenByUser
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
      count
    }
  }
`

export const GET_PUBLIC_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfilePublic(
    $uid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfilePublic(uid: $uid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

export const GET_PROFILE_BY_UUID = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileByUuid(
    $uuid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfileByUuid(uuid: $uuid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

export const GET_PUBLIC_PROFILE_BY_UUID = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfilePublicByUuid(
    $uuid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfilePublicByUuid(uuid: $uuid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

export const UPDATE_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  mutation UserProfileUpdate($input: UserProfileUpdateInput!, $language: String) {
    userProfileUpdate(input: $input) {
      profile {
        ...CoreUserProfileFields
        # Cascade reads: when input includes profileVisibility=private, brooklyn
        # cascades favorites/watchLater visibility transactionally. Selecting
        # them here lets Apollo write the new values to the Playlist:<id>
        # entities in cache without a refetch.
        favorites {
          id
          visibility
        }
        watchLater {
          id
          visibility
        }
      }
    }
  }
`

/**
 * Reserved for future user-follow feature.
 * Not currently used in any component — kept so the mutation is ready
 * when user-to-user following is implemented.
 */
export const CREATE_FOLLOW = gql`
  mutation UserProfileFollow($uid: ID!) {
    userProfileFollow(uid: $uid) {
      profile {
        uid
        uuid
        followerCount
        followingCount
        viewerContext {
          isOwner
          isFollowing
          isFollowedBy
          visibilityLevel
        }
      }
    }
  }
`

/**
 * Reserved for future user-follow feature.
 * Not currently used in any component — kept so the mutation is ready
 * when user-to-user following is implemented.
 */
export const DELETE_FOLLOW = gql`
  mutation UserProfileUnfollow($uid: ID!) {
    userProfileUnfollow(uid: $uid) {
      profile {
        uid
        uuid
        followerCount
        followingCount
        viewerContext {
          isOwner
          isFollowing
          isFollowedBy
          visibilityLevel
        }
      }
    }
  }
`

export const GET_FOLLOWERS = gql`
  ${USER_PROFILE_SUMMARY_FIELDS}
  query UserProfileFollowers($uid: ID!, $first: Int = 20, $after: String, $searchQuery: String) {
    userProfileFollowers(uid: $uid, first: $first, after: $after, searchQuery: $searchQuery) {
      edges {
        node {
          ...UserProfileSummaryFields
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_FOLLOWING = gql`
  ${USER_PROFILE_SUMMARY_FIELDS}
  query UserProfileFollowing($uid: ID!, $first: Int = 20, $after: String, $searchQuery: String) {
    userProfileFollowing(uid: $uid, first: $first, after: $after, searchQuery: $searchQuery) {
      edges {
        node {
          ...UserProfileSummaryFields
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_SETTINGS_PAGE_DATA = gql`
  ${CORE_USER_PROFILE_FIELDS}
  query SettingsPageData($language: String) {
    userProfileCurrent(language: $language) {
      ...CoreUserProfileFields
    }
    userTagOptionsList(language: $language) {
      id
      tag
      category
      label
    }
  }
`

export const GET_USER_TAG_OPTIONS = gql`
  query UserTagOptionsList($language: String) {
    userTagOptionsList(language: $language) {
      id
      tag
      category
      label
    }
  }
`

/**
 * Centers-bundle tags linked from topic-center (EN rows).
 * Aligns with userProfileUpdate.centerContentIds.
 */
// Topic tag labels (centers / canonical interests) come pre-translated from
// the backend. Caller must pass the user's primary app language with English
// fallback (e.g. `[language, 'en']`), mirroring GET_PLAYLIST_BY_ID and other
// content queries. Without a real language variable here, every locale would
// see the English `title` (the prior `["en"]` hardcode was the source of the
// non-translating community-profile-topic-tag labels).
export const GET_CANONICAL_CENTERS = gql`
  query CanonicalCenters($language: [String]!) {
    contentList(
      where: { contentType: { eq: "tag" }, bundle: { eq: "centers" } }
      contentReferencesFrom: [{ type: "topic-center" }]
      language: $language
      limit: 100
    ) {
      content {
        contentId
        title
      }
      count
    }
  }
`

export const GET_MY_PLAYLISTS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query MyPlaylists($language: [String]!) {
    playlistList(userCurrent: true, includePrivate: true, limit: 100) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        followerCount
        description
        playlistItems {
          ...PlaylistItemContentFields
        }
      }
    }
  }
`

// network-only: user-scoped, lists which playlists I own.
// Shell shape: playlistItems carry only { id, contentId, contentType,
// playlistPosition }; full Content fields are hydrated separately via
// GET_CONTENT (cache-first) at the carousel layer. Limit 24 pairs with the
// pagination wired in useProfilePlaylists.
export const GET_MY_PLAYLISTS_SHELL = gql`
  query MyPlaylistsShell($limit: Int = 24, $offset: Int = 0) {
    playlistList(
      userCurrent: true
      includePrivate: true
      limit: $limit
      offset: $offset
    ) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        followerCount
        description
        playlistItems {
          id
          contentId
          titleGroupId
          contentType
          playlistPosition
        }
      }
    }
  }
`

export const GET_PLAYLISTS_WITH_ITEMS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query PlaylistsWithItems($playlistIds: [ID]!, $language: [String]!) {
    playlistList(where: { id: { in: $playlistIds } }) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        playlistItems {
          ...PlaylistItemContentFields
        }
      }
    }
  }
`

export const GET_PLAYLIST_BY_ID = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query PlaylistById($playlistId: ID!, $language: [String]!) {
    playlistList(where: { id: { eq: $playlistId } }) {
      playlists {
        id
        uid
        playlistName
        playlistType
        visibility
        description
        itemCount
        followerCount
        isFollowing
        userProfile {
          uuid
          displayName
          avatar {
            url
            small
            card
          }
        }
        playlistItems {
          ...PlaylistItemContentFields
          content(language: $language) {
            featureMedia {
              contentPolicy {
                shareAllowed
                accessCheck {
                  countryAllowed
                }
              }
            }
          }
        }
      }
    }
  }
`

export const GET_DISCOVER_PLAYLIST_DETAILS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query DiscoverPlaylistDetails($playlistIds: [ID]!, $language: [String]!) {
    playlistList(where: { id: { in: $playlistIds } }) {
      playlists {
        id
        uid
        playlistName
        playlistType
        visibility
        description
        itemCount
        followerCount
        isFollowing
        userProfile {
          uuid
          displayName
          avatar {
            url
            small
            card
          }
        }
        playlistItems {
          ...PlaylistItemContentFields
        }
      }
    }
  }
`

export const GET_USER_META_TAGS = gql`
  query ProfileMetaTags {
    userMetaTagList(userCurrent: true) {
      userMetaTags {
        id
        name
        tagType
        viewCount
        viewSeconds
        weight
      }
    }
  }
`

export const GET_USER_HISTORY = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query GetUserHistory(
    $limit: Int
    $offset: Int
    $contentLanguage: [String]
  ) {
    userMediaPositionList(
      userCurrent: true
      publishedMediaOnly: true
      limit: $limit
      offset: $offset
      order: [["lastPlayed", "DESC"]]
      where: {
        hiddenByUser: { eq: false }
        lastPlayed: { gt: "0" }
        watchedSum: { gte: "15" }
      }
    ) {
      userMediaPositions {
        mediaId
        position
        lastPlayed
        watchedSum
        hiddenByUser
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
      count
    }
  }
`

export const CREATE_PROFILE_PICTURE_UPLOAD = gql`
  mutation RequestProfilePictureUpload($fileExtension: String!) {
    requestProfilePictureUpload(fileExtension: $fileExtension) {
      uploadUrl
      imageUuid
      expiresAt
    }
  }
`

export const CREATE_PROFILE_PICTURE_CONFIRM = gql`
  mutation ConfirmProfilePictureUpload($imageUuid: ID!, $imageExtension: String!) {
    confirmProfilePictureUpload(imageUuid: $imageUuid, imageExtension: $imageExtension) {
      success
      profilePicture {
        url
        small
        card
      }
    }
  }
`

export const DELETE_PROFILE_PICTURE = gql`
  mutation DeleteProfilePicture {
    deleteProfilePicture {
      success
    }
  }
`

export const UPDATE_PLAYLIST_VISIBILITY = gql`
  mutation UpdatePlaylistVisibility($playlistId: ID!, $visibility: PlaylistVisibility!) {
    updatePlaylistVisibility(playlistId: $playlistId, visibility: $visibility) {
      playlist {
        id
        visibility
      }
    }
  }
`

export const DELETE_PLAYLIST = gql`
  mutation DeletePlaylist($playlistType: String!) {
    playlistDelete(playlist: { playlistType: $playlistType }) {
      playlist {
        id
        uid
        playlistType
        playlistName
      }
    }
  }
`

// IMPORTANT: keep this response shape symmetric with `UNFOLLOW_PLAYLIST` —
// just the fields the React handlers read (`followerCount`, `isFollowing`).
// Returning a richer Playlist (especially nested `userInfo`) used to make
// Apollo replace the embedded `userInfo` on the normalized `Playlist:<id>`
// cache entry, drop `userInfo.avatar`, and trigger an automatic
// `PlaylistById` refetch on the share page (huge payload, blocked the UI
// until it completed). Symmetric minimal shape avoids that cache thrash and
// matches the pattern Discover/Following already use successfully.
export const FOLLOW_PLAYLIST = gql`
  mutation FollowPlaylist($playlistId: ID!) {
    followPlaylist(playlistId: $playlistId) {
      id
      playlistName
      followerCount
      isFollowing
    }
  }
`

export const UNFOLLOW_PLAYLIST = gql`
  mutation UnfollowPlaylist($playlistId: ID!) {
    unfollowPlaylist(playlistId: $playlistId) {
      id
      playlistName
      followerCount
      isFollowing
    }
  }
`

export const ADD_PLAYLIST_ITEM = gql`
  mutation AddPlaylistItem(
    $contentId: Int!
    $contentType: String!
    $playlistType: String!
    $playlistId: ID
  ) {
    playlistCreatePlaylistItem(
      playlistItem: { contentId: $contentId, contentType: $contentType }
      playlist: { id: $playlistId, playlistType: $playlistType }
      createPlaylistIfNotExist: true
    ) {
      playlistItem {
        id
        playlistId
        contentType
        contentId
        playlistPosition
      }
    }
  }
`

export const REMOVE_PLAYLIST_ITEM = gql`
  mutation RemovePlaylistItem(
    $contentId: Int!
    $contentType: String!
    $playlistId: ID!
    $playlistType: String!
  ) {
    playlistDeletePlaylistItem(
      playlistItem: { contentId: $contentId, contentType: $contentType }
      playlist: { id: $playlistId, playlistType: $playlistType }
    ) {
      playlistItem {
        id
        playlistId
        contentType
        contentId
        playlistPosition
        playlist {
          playlistType
          playlistName
          id
        }
      }
    }
  }
`

/**
 * Update a playlist item's position.  Used by the Move Earlier / Move Later
 * controls on owned playlists.
 *
 * Semantic: `playlistPosition` is an integer; highest = top of list,
 * 0 = bottom.  With `reorderPlaylist: true` (schema default) the server
 * renormalizes other rows so positions stay dense.
 *
 * The Brooklyn resolver requires the row's stored `contentId` and
 * `contentType` even though the GraphQL schema marks them nullable —
 * same row-key pair every other playlist mutation uses.  Without those
 * fields the request fails with `"contentId" is required`.
 */
export const UPDATE_PLAYLIST_ITEM = gql`
  mutation UpdatePlaylistItem(
    $playlistItemId: ID!
    $contentId: Int!
    $contentType: String!
    $playlistPosition: Int!
    $playlistId: ID!
    $playlistType: String!
  ) {
    playlistUpdatePlaylistItem(
      playlistItem: {
        id: $playlistItemId
        contentId: $contentId
        contentType: $contentType
        playlistPosition: $playlistPosition
      }
      playlist: { id: $playlistId, playlistType: $playlistType }
      reorderPlaylist: true
    ) {
      playlistItem {
        id
        playlistId
        contentId
        contentType
        playlistPosition
      }
    }
  }
`

export const GET_COMMUNITY_LEADERBOARD = gql`
  query CommunityLeaderboard($timeWindow: LeaderboardTimeWindow, $limit: Int) {
    communityLeaderboard(timeWindow: $timeWindow, limit: $limit) {
      user {
        uid
      }
      reactionCount
    }
  }
`

export const GET_DISCOVER_PLAYLISTS = gql`
  query DiscoverPlaylists(
    $query: String!
    $limit: Int
    $after: String
    $visibility: [PlaylistVisibility]
    $order: PlaylistSearchOrder
  ) {
    playlistSearch(
      query: $query
      limit: $limit
      after: $after
      visibility: $visibility
      order: $order
    ) {
      edges {
        node {
          id
          playlistName
          playlistType
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_FOLLOWED_PLAYLISTS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query FollowedPlaylists($uid: ID!, $limit: Int, $after: String, $language: [String]!) {
    followedPlaylists(uid: $uid, limit: $limit, after: $after) {
      edges {
        node {
          id
          playlistName
          playlistType
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
          }
          playlistItems {
            ...PlaylistItemContentFields
          }
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

// cache-first: engagement counts are eventually consistent across users.
// Backend bulk endpoint (gaia-brooklyn-api Query.playlistEngagement) takes
// a list of playlist IDs and returns one row each. Used to move
// followerCount off the shell into a hover / post-paint lookup.
export const PLAYLIST_ENGAGEMENT_BULK = gql`
  query PlaylistEngagementBulk($playlistIds: [ID!]!) {
    playlistEngagement(playlistIds: $playlistIds) {
      playlistId
      followerCount
    }
  }
`

// network-only: user-scoped, lists which playlists I follow.
// Shell shape: same pattern as GET_MY_PLAYLISTS_SHELL — playlistItems carry
// only IDs; tile content is hydrated via GET_CONTENT(cache-first).
// userProfile is cheap with only { uuid, displayName } selected.
export const GET_FOLLOWED_PLAYLISTS_SHELL = gql`
  query FollowedPlaylistsShell($uid: ID!, $limit: Int = 24, $after: String, $searchQuery: String) {
    followedPlaylists(uid: $uid, limit: $limit, after: $after, searchQuery: $searchQuery) {
      edges {
        node {
          id
          playlistName
          playlistType
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
          }
          playlistItems {
            id
            contentId
            titleGroupId
            contentType
            playlistPosition
          }
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const SEARCH_MY_PLAYLISTS = gql`
  query SearchMyPlaylists($searchTerm: String!) {
    playlistList(userCurrent: true, includePrivate: true, searchTerm: $searchTerm) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        description
      }
    }
  }
`

export const GET_COMMENT_COUNT = gql`
  query ProfileCommentCount {
    commentList(userCurrent: true, limit: 1) {
      count
    }
  }
`

// Offset-paginated. searchTerm narrows by playlistName server-side; episode
// titles are filtered client-side on the loaded set.
export const GET_MEMBER_PLAYLISTS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query GetMemberPlaylists(
    $uid: ID!
    $language: [String]!
    $visibility: [PlaylistVisibility!]!
    $limit: Int = 50
    $offset: Int = 0
    $searchTerm: String
  ) {
    playlistList(
      where: { uid: { eq: $uid } }
      visibility: $visibility
      order: [["updatedAt", "DESC"]]
      limit: $limit
      offset: $offset
      searchTerm: $searchTerm
    ) {
      count
      playlists {
        id
        playlistName
        playlistType
        visibility
        description
        itemCount
        followerCount
        isFollowing
        playlistItems {
          ...PlaylistItemContentFields
        }
      }
    }
  }
`

// ── Identity Vibe ─────────────────────────────────────────────────────

export const GENERATE_IDENTITY_VIBE = gql`
  mutation GenerateVibes($input: OracleIdentityVibeGenerateInput) {
    oracleIdentityVibeGenerate(input: $input) {
      success
      errors
      vibeLines
    }
  }
`

export const SELECT_IDENTITY_VIBE = gql`
  mutation SelectVibe($input: OracleIdentityVibeSelectInput!) {
    oracleIdentityVibeSelect(input: $input) {
      success
      errors
      oracleIdentityVibe {
        id
        vibe
        description
        generatedAt
      }
    }
  }
`

export const ORACLE_SEARCH_QUERY = gql`
  query OracleSearch(
    $input: OracleSearchInput!
    $language: [String]
    $learnerLevelLanguage: String!
  ) {
    oracleSearch(input: $input) {
      success
      errors
      executionTime
      text
      contentText
      playlistText
      content {
        contentId
        brooklynId
        contentType
        title
        score
        content {
          ...OracleContentFields
          seasonsCount(language: $language)
          episodesCount(language: $language, content: [{ publishedState: PUBLISHED_NOW }])
        }
      }
      playlists {
        id
        title
        score
        playlist {
          id
          playlistName
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
            avatar {
              url
              small
              card
            }
          }
          playlistItems {
            id
            contentId
            contentType
            playlistPosition
            content(language: $language) {
              ...OracleContentFields
            }
          }
        }
      }
    }
  }
  ${ORACLE_CONTENT_FIELDS}
`
